import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  Badge,
  Card,
  Tab,
  Tabs,
  ProgressBar,
  InputGroup,
} from "react-bootstrap";
import {
  IconUser,
  IconUsers,
  IconSettings,
  IconToggleLeft,
  IconToggleRight,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconMail,
  IconPuzzle,
  IconSearch,
  IconRobot,
  IconMessage,
  IconUsersGroup,
  IconBolt,
  IconPlug,
  IconPackage,
  IconDots,
} from "@tabler/icons-react";
import "../css/editSubaccountModal.css";
import Swal from "sweetalert2";
import SubaccountService from "../../../services/SubaccountServices";
import { resendActivationEmail } from "../../Users/services/UserServicesV2";
import UserContext from "../../../contexts/UserContext";

const EditSubaccountModal = ({ 
  show, 
  onHide, 
  subaccountId, 
  onSuccess 
}) => {
  const { user, isOrganization, getOrganizationInfo, getUserPlan } = useContext(UserContext);
  
  // Função para obter limite de usuários baseado no plano
  const getUserLimitByPlan = () => {
    const plan = getUserPlan();
    switch (plan) {
      case 3: // Starter
        return 1;
      case 4: // PRO
        return 3;
      case 2: // Business/Founder
      case 5: // Scale
        return 5;
      default:
        return 1;
    }
  };
  
  // Estados principais
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subaccountData, setSubaccountData] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  
  // Estados para edição
  const [userLimit, setUserLimit] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  // Estados para features
  const [featuresData, setFeaturesData] = useState(null);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [featuresSearch, setFeaturesSearch] = useState("");
  const [pendingChanges, setPendingChanges] = useState({});

  // Carregar dados da subconta quando o modal abrir
  useEffect(() => {
    if (show && subaccountId) {
      loadSubaccountData();
    } else {
      resetModal();
    }
  }, [show, subaccountId]);

  const resetModal = () => {
    setSubaccountData(null);
    setActiveTab("details");
    setUserLimit("");
    setValidationErrors({});
    setFeaturesData(null);
    setFeaturesSearch("");
    setPendingChanges({});
  };

  const loadSubaccountData = async () => {
    try {
      setLoading(true);
      const data = await SubaccountService.getSubaccountDetails(subaccountId);
      setSubaccountData(data);
      setUserLimit(data.max_users_allowed?.toString() || "");
    } catch (error) {
      console.error("Erro ao carregar dados da subconta:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os dados da subconta.",
      });
      onHide();
    } finally {
      setLoading(false);
    }
  };

  // Carregar features da subconta
  const loadFeaturesData = async () => {
    try {
      setFeaturesLoading(true);
      const data = await SubaccountService.getSubaccountFeatures(subaccountId);
      setFeaturesData(data);
      setPendingChanges({});
    } catch (error) {
      console.error("Erro ao carregar features da subconta:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar as features da subconta.",
      });
    } finally {
      setFeaturesLoading(false);
    }
  };

  // Carregar features quando aba for selecionada
  useEffect(() => {
    if (activeTab === "features" && subaccountId && !featuresData) {
      loadFeaturesData();
    }
  }, [activeTab, subaccountId]);

  // Toggle de feature (local, sem salvar)
  const handleFeatureToggle = (featureId, currentEnabled) => {
    const newEnabled = !currentEnabled;

    // Atualizar pendingChanges
    setPendingChanges(prev => {
      const original = featuresData?.features?.find(f => f.id === featureId);
      const originalEnabled = original?.is_enabled ?? false;

      // Se voltou ao valor original, remover das mudanças pendentes
      if (newEnabled === originalEnabled) {
        const { [featureId]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [featureId]: newEnabled,
      };
    });
  };

  // Verificar se feature tem mudança pendente
  const getFeatureEnabled = (feature) => {
    if (pendingChanges.hasOwnProperty(feature.id)) {
      return pendingChanges[feature.id];
    }
    return feature.is_enabled;
  };

  // Salvar mudanças de features
  const handleSaveFeatures = async () => {
    if (!canEdit() || Object.keys(pendingChanges).length === 0) return;

    const featuresToUpdate = Object.entries(pendingChanges).map(([id, is_enabled]) => ({
      id: parseInt(id, 10),
      is_enabled,
    }));

    const result = await Swal.fire({
      title: "Salvar Alterações",
      html: `
        <p>Deseja salvar as alterações nas features?</p>
        <p><small class="text-muted">${featuresToUpdate.length} feature(s) serão atualizadas</small></p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#007bff",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, salvar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        setFeaturesSaving(true);
        await SubaccountService.updateSubaccountFeatures(subaccountId, featuresToUpdate);

        // Recarregar features para obter estado atualizado
        await loadFeaturesData();

        Swal.fire({
          icon: "success",
          title: "Sucesso",
          text: "Features atualizadas com sucesso!",
          timer: 2000,
          showConfirmButton: false,
        });

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error("Erro ao salvar features:", error);
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: error.response?.data?.error || "Não foi possível salvar as alterações.",
        });
      } finally {
        setFeaturesSaving(false);
      }
    }
  };

  // Descartar mudanças
  const handleDiscardChanges = () => {
    setPendingChanges({});
  };

  // Verificar permissões
  const canEdit = () => {
    return isOrganization() && user?.is_superuser && user?.superuser_level === 1;
  };

  const canToggleStatus = () => {
    return canEdit();
  };

  // Alternar status da subconta
  const handleToggleStatus = async () => {
    if (!canToggleStatus()) {
      Swal.fire({
        icon: "error",
        title: "Sem Permissão",
        text: "Apenas donos de organização podem alterar o status de clientes.",
      });
      return;
    }

    const action = subaccountData.subaccount_active ? "desativar" : "ativar";
    
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Subconta`,
      text: `Tem certeza que deseja ${action} esta subconta?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: subaccountData.subaccount_active ? "#dc3545" : "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Sim, ${action}`,
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        const response = await SubaccountService.toggleSubaccountStatus(subaccountId);
        
        setSubaccountData(prev => ({
          ...prev,
          subaccount_active: response.is_active
        }));

        Swal.fire({
          icon: "success",
          title: "Sucesso",
          text: response.message,
          timer: 2000,
          showConfirmButton: false,
        });

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error("Erro ao alterar status:", error);
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: error.response?.data?.error || "Não foi possível alterar o status da subconta.",
        });
      } finally {
        setSaving(false);
      }
    }
  };

  // Atualizar limite de usuários
  const handleUpdateUserLimit = async () => {
    if (!canEdit()) {
      Swal.fire({
        icon: "error",
        title: "Sem Permissão",
        text: "Apenas donos de organização podem alterar limites de usuários.",
      });
      return;
    }

    // Validar entrada
    const newLimit = parseInt(userLimit, 10);
    const organization = getOrganizationInfo();
    
    const validation = SubaccountService.validateUserLimit(
      subaccountData.users?.length || 0,
      newLimit,
      organization?.max_users_per_account
    );

    if (!validation.isValid) {
      setValidationErrors({ userLimit: validation.errors.join(", ") });
      return;
    }

    setValidationErrors({});

    const result = await Swal.fire({
      title: "Atualizar Limite de Usuários",
      text: `Alterar limite de ${subaccountData.max_users_allowed} para ${newLimit} usuários?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#007bff",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, atualizar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        setSaving(true);
        const response = await SubaccountService.updateUserLimit(subaccountId, newLimit);
        
        setSubaccountData(prev => ({
          ...prev,
          max_users_allowed: response.max_users_allowed,
          current_users_count: response.current_users,
        }));

        Swal.fire({
          icon: "success",
          title: "Sucesso",
          text: response.message,
          timer: 2000,
          showConfirmButton: false,
        });

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error("Erro ao atualizar limite:", error);
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: error.response?.data?.error || "Não foi possível atualizar o limite de usuários.",
        });
      } finally {
        setSaving(false);
      }
    }
  };

  // Reenviar email de ativação
  const handleResendActivationEmail = async () => {
    if (!canEdit()) {
      Swal.fire({
        icon: "error",
        title: "Sem Permissão",
        text: "Apenas donos de organização podem reenviar emails de ativação.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Reenviar Email de Ativação",
      html: `
        <p>Deseja reenviar o email de ativação para:</p>
        <p><strong>${subaccountData.company_name}</strong></p>
        <p><small class="text-muted">${subaccountData.email}</small></p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#007bff",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, reenviar!",
      cancelButtonText: "Cancelar",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await resendActivationEmail(subaccountId);
          return response;
        } catch (error) {
          Swal.showValidationMessage(
            `Erro: ${
              error.response?.data?.error ||
              error.message ||
              "Falha ao reenviar email"
            }`
          );
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed && result.value) {
      const response = result.value;

      Swal.fire({
        icon: "success",
        title: "Email Reenviado!",
        html: `
          <p>Email de ativação reenviado com sucesso para:</p>
          <p><strong>${response.data.sent_to}</strong></p>
          <p><small class="text-muted">O token expira em: ${new Date(
            response.data.token_expires
          ).toLocaleString("pt-BR")}</small></p>
        `,
        confirmButtonText: "OK",
      });
    }
  };

  // Renderizar aba de detalhes
  const renderDetailsTab = () => (
    <div className="mt-3">
      <Row>
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">
                <IconUser size={16} className="me-2" />
                Informações da Empresa
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Nome da Empresa:</strong>
                <div>{subaccountData.company_name}</div>
              </div>
              {subaccountData.brand_name && (
                <div className="mb-3">
                  <strong>Nome da Marca:</strong>
                  <div>{subaccountData.brand_name}</div>
                </div>
              )}
              <div className="mb-3">
                <strong>Email:</strong>
                <div>{subaccountData.email}</div>
              </div>
              <div className="mb-3">
                <strong>Status:</strong>
                <div>
                  <Badge bg={subaccountData.subaccount_active ? "success" : "secondary"}>
                    {subaccountData.subaccount_active ? "Ativa" : "Inativa"}
                  </Badge>
                </div>
              </div>
              <div>
                <strong>Criada em:</strong>
                <div>{new Date(subaccountData.created_at).toLocaleDateString("pt-BR")}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">
                <IconUsers size={16} className="me-2" />
                Usuários e Limites
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <strong>Usuários Ativos:</strong>
                  <span>
                    {subaccountData.users?.length || 0} / {subaccountData.max_users_allowed}
                  </span>
                </div>
                <ProgressBar 
                  now={((subaccountData.users?.length || 0) / subaccountData.max_users_allowed) * 100}
                  variant={
                    ((subaccountData.users?.length || 0) / subaccountData.max_users_allowed) > 0.8 
                      ? "warning" 
                      : "primary"
                  }
                />
              </div>
              
              {subaccountData.users && subaccountData.users.length > 0 && (
                <div>
                  <strong>Lista de Usuários:</strong>
                  <div className="mt-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {subaccountData.users.map(user => (
                      <div key={user.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                        <div>
                          <div className="fw-medium">{user.name}</div>
                          <small className="text-muted">{user.email}</small>
                        </div>
                        <Badge bg={user.is_superuser ? "primary" : "secondary"} pill>
                          {user.superuser_level === 1 ? "Admin" : 
                           user.superuser_level === 2 ? "Gerente" :
                           user.superuser_level === 3 ? "Supervisor" : "Atendente"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Features da subconta */}
      {subaccountData.features && subaccountData.features.length > 0 && (
        <Card className="mt-3">
          <Card.Header>
            <h6 className="mb-0">Features Ativas</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              {subaccountData.features.map(feature => (
                <Col md={6} lg={4} key={feature.id} className="mb-3">
                  <div className="d-flex align-items-center">
                    <Badge 
                      bg={feature.is_enabled ? "success" : "secondary"} 
                      className="me-2"
                    >
                      {feature.is_enabled ? <IconCheck size={12} /> : <IconX size={12} />}
                    </Badge>
                    <div>
                      <div className="fw-medium">{feature.name}</div>
                      {feature.usage_limit && (
                        <small className="text-muted">
                          {feature.current_usage || 0} / {feature.usage_limit}
                        </small>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}
    </div>
  );

  // Renderizar aba de configurações
  const renderSettingsTab = () => (
    <div className="mt-3">
      {!canEdit() && (
        <Alert variant="warning" className="mb-3">
          <IconAlertTriangle size={16} className="me-2" />
          Apenas donos de organização podem alterar configurações de clientes.
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Status da Subconta</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="fw-medium">
                    Status: 
                    <Badge 
                      bg={subaccountData.subaccount_active ? "success" : "secondary"}
                      className="ms-2"
                    >
                      {subaccountData.subaccount_active ? "Ativa" : "Inativa"}
                    </Badge>
                  </div>
                  <small className="text-muted">
                    {subaccountData.subaccount_active 
                      ? "A subconta está ativa e funcionando normalmente"
                      : "A subconta está inativa e não pode ser acessada"}
                  </small>
                </div>
                <Button
                  variant={subaccountData.subaccount_active ? "outline-danger" : "outline-success"}
                  size="sm"
                  onClick={handleToggleStatus}
                  disabled={!canToggleStatus() || saving}
                >
                  {saving ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      {subaccountData.subaccount_active ? (
                        <IconToggleRight size={16} className="me-1" />
                      ) : (
                        <IconToggleLeft size={16} className="me-1" />
                      )}
                      {subaccountData.subaccount_active ? "Desativar" : "Ativar"}
                    </>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Limite de Usuários</h6>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group>
                  <Form.Label>Máximo de usuários permitidos</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="1"
                      max={getUserLimitByPlan()}
                      value={userLimit}
                      onChange={(e) => {
                        const maxLimit = getUserLimitByPlan();
                        if (e.target.value > maxLimit) {
                          Swal.fire({
                            icon: "warning",
                            title: "Limite Excedido",
                            text: `O limite máximo permitido para seu plano é ${maxLimit} usuário${maxLimit > 1 ? 's' : ''}.`,
                          });
                        } else {
                          setUserLimit(e.target.value);
                        }
                      }}
                      disabled={!canEdit()}
                      isInvalid={!!validationErrors.userLimit}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={handleUpdateUserLimit}
                      disabled={
                        !canEdit() || 
                        saving || 
                        userLimit === subaccountData.max_users_allowed?.toString()
                      }
                    >
                      {saving ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Atualizar"
                      )}
                    </Button>
                  </InputGroup>
                  {validationErrors.userLimit && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.userLimit}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Usuários atuais: {subaccountData.users?.length || 0} | 
                    Máximo permitido para seu plano: {getUserLimitByPlan()} usuário{getUserLimitByPlan() > 1 ? 's' : ''}
                  </Form.Text>
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Email de Ativação</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="fw-medium">
                    Reenviar Email de Ativação
                  </div>
                  <small className="text-muted">
                    Envia um novo email de ativação para: {subaccountData.email}
                  </small>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleResendActivationEmail}
                  disabled={!canEdit() || saving}
                >
                  {saving ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <IconMail size={16} className="me-1" />
                      Reenviar Email
                    </>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );

  // Renderizar aba de features
  const renderFeaturesTab = () => {
    // Agrupar features por categoria
    const groupedFeatures = {};
    const features = featuresData?.features || [];

    features.forEach(feature => {
      const category = feature.category || "outros";
      if (!groupedFeatures[category]) {
        groupedFeatures[category] = [];
      }
      groupedFeatures[category].push(feature);
    });

    // Filtrar features por busca
    const filterFeatures = (featuresArray) => {
      if (!featuresSearch.trim()) return featuresArray;
      const search = featuresSearch.toLowerCase();
      return featuresArray.filter(f =>
        f.name.toLowerCase().includes(search) ||
        f.slug.toLowerCase().includes(search) ||
        f.description?.toLowerCase().includes(search)
      );
    };

    // Configuração de categorias com ícones e nomes
    const categoryConfig = {
      agents: { name: "Agentes IA", icon: IconRobot },
      service: { name: "Atendimento", icon: IconMessage },
      clients: { name: "Gestão de Clientes", icon: IconUsersGroup },
      automations: { name: "Automações", icon: IconBolt },
      integrations: { name: "Integrações", icon: IconPlug },
      settings: { name: "Configurações", icon: IconSettings },
      users: { name: "Usuários", icon: IconUsers },
      products: { name: "Produtos", icon: IconPackage },
      outros: { name: "Outros", icon: IconDots },
    };

    // Ordem das categorias para exibição
    const categoryOrder = ["agents", "service", "clients", "automations", "integrations", "users", "products", "settings", "outros"];

    const hasChanges = Object.keys(pendingChanges).length > 0;
    const enabledCount = features.filter(f => getFeatureEnabled(f)).length;
    const disabledCount = features.filter(f => !getFeatureEnabled(f)).length;

    return (
      <div className="mt-3">
        {!canEdit() && (
          <Alert variant="warning" className="mb-3 d-flex align-items-center">
            <IconAlertTriangle size={18} className="me-2 flex-shrink-0" />
            <span>Apenas administradores da organização podem alterar as features desta subconta.</span>
          </Alert>
        )}

        {featuresLoading ? (
          <div className="features-tab__loading">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="features-tab__loading-text">Carregando features disponíveis...</p>
          </div>
        ) : featuresData && features.length > 0 ? (
          <>
            {/* Header com busca e ações */}
            <div className="features-tab__header">
              <Row className="align-items-center">
                <Col md={6} lg={5}>
                  <div className="features-tab__search-wrapper">
                    <IconSearch size={18} className="features-tab__search-icon" />
                    <Form.Control
                      type="text"
                      placeholder="Buscar por nome ou descrição..."
                      value={featuresSearch}
                      onChange={(e) => setFeaturesSearch(e.target.value)}
                    />
                  </div>
                </Col>
                <Col md={6} lg={7} className="text-md-end mt-3 mt-md-0">
                  <div className="features-tab__actions">
                    {hasChanges && (
                      <>
                        <div className="features-tab__changes-badge">
                          <IconAlertTriangle size={14} />
                          {Object.keys(pendingChanges).length} alteração{Object.keys(pendingChanges).length > 1 ? "ões" : ""} pendente{Object.keys(pendingChanges).length > 1 ? "s" : ""}
                        </div>
                        <button
                          className="features-tab__btn features-tab__btn--discard"
                          onClick={handleDiscardChanges}
                          disabled={featuresSaving}
                        >
                          <IconX size={16} />
                          Descartar
                        </button>
                        <button
                          className="features-tab__btn features-tab__btn--save"
                          onClick={handleSaveFeatures}
                          disabled={featuresSaving || !canEdit()}
                        >
                          {featuresSaving ? (
                            <>
                              <Spinner animation="border" size="sm" />
                              Salvando...
                            </>
                          ) : (
                            <>
                              <IconCheck size={16} />
                              Salvar Alterações
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Lista de features por categoria */}
            {categoryOrder.map(category => {
              const categoryFeatures = groupedFeatures[category];
              if (!categoryFeatures) return null;

              const filteredFeatures = filterFeatures(categoryFeatures);
              if (filteredFeatures.length === 0) return null;

              const config = categoryConfig[category] || categoryConfig.outros;
              const CategoryIcon = config.icon;
              const enabledInCategory = filteredFeatures.filter(f => getFeatureEnabled(f)).length;

              return (
                <div key={category} className="features-tab__category">
                  <div className="features-tab__category-header">
                    <h6 className="features-tab__category-title">
                      <div className={`features-tab__category-icon features-tab__category-icon--${category}`}>
                        <CategoryIcon size={18} />
                      </div>
                      {config.name}
                    </h6>
                    <span className="features-tab__category-count">
                      {enabledInCategory}/{filteredFeatures.length} ativas
                    </span>
                  </div>
                  <div className="features-tab__category-body">
                    <div className="features-tab__grid">
                      {filteredFeatures.map(feature => {
                        const isEnabled = getFeatureEnabled(feature);
                        const hasChange = pendingChanges.hasOwnProperty(feature.id);

                        return (
                          <div
                            key={feature.id}
                            className={`features-tab__item ${
                              isEnabled ? "features-tab__item--enabled" : "features-tab__item--disabled"
                            } ${hasChange ? "features-tab__item--changed" : ""}`}
                            onClick={() => canEdit() && !featuresSaving && handleFeatureToggle(feature.id, isEnabled)}
                          >
                            <div className="features-tab__item-toggle" onClick={(e) => e.stopPropagation()}>
                              <Form.Check
                                type="switch"
                                id={`feature-switch-${feature.id}`}
                                checked={isEnabled}
                                onChange={() => handleFeatureToggle(feature.id, isEnabled)}
                                disabled={!canEdit() || featuresSaving}
                              />
                            </div>
                            <div className="features-tab__item-content">
                              <div className="features-tab__item-name">
                                {feature.name}
                              </div>
                              {feature.description && (
                                <div className="features-tab__item-description">
                                  {feature.description}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Resumo */}
            <div className="features-tab__summary">
              <div className="features-tab__summary-grid">
                <div className="features-tab__summary-item">
                  <span className="features-tab__summary-value features-tab__summary-value--enabled">
                    {enabledCount}
                  </span>
                  <span className="features-tab__summary-label">Habilitadas</span>
                </div>
                <div className="features-tab__summary-item">
                  <span className="features-tab__summary-value features-tab__summary-value--disabled">
                    {disabledCount}
                  </span>
                  <span className="features-tab__summary-label">Desabilitadas</span>
                </div>
                <div className="features-tab__summary-item">
                  <span className="features-tab__summary-value features-tab__summary-value--total">
                    {features.length}
                  </span>
                  <span className="features-tab__summary-label">Total</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="features-tab__empty">
            <IconPuzzle size={48} className="features-tab__empty-icon" />
            <h5 className="features-tab__empty-title">Nenhuma feature encontrada</h5>
            <p className="features-tab__empty-text">
              Esta subconta ainda não possui features configuradas.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <IconSettings size={20} className="me-2" />
          Editar Subconta
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando dados da subconta...</p>
          </div>
        ) : subaccountData ? (
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="details" title="Detalhes">
              {renderDetailsTab()}
            </Tab>
            <Tab eventKey="features" title={<><IconPuzzle size={14} className="me-1" />Features</>}>
              {renderFeaturesTab()}
            </Tab>
            <Tab eventKey="settings" title="Configurações">
              {renderSettingsTab()}
            </Tab>
          </Tabs>
        ) : (
          <Alert variant="warning">
            Não foi possível carregar os dados da subconta.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSubaccountModal;